<template>
  <div class="container special-price-page">
    <div class="columns box-title is-12">
      <div class="column is-8">
        <h4 class="has-text-left">
          <i class="fal fa-tags"></i>
          {{ $t('specialPrice.page_title') }}
          <span class="countTotal">({{ pagination.total || 0 }})</span>
        </h4>
      </div>
      <div class="column is-4">
        <vs-button circle icon flat @click="openForm(null)"
                   style="padding-left: 8px; padding-right: 8px; float: right">
          <i class="fal fa-plus-circle"></i>
        </vs-button>
      </div>
    </div>

    <p class="page-hint">
      {{ $t('specialPrice.page_hint') }}
    </p>

    <CacheStaleNotice :visible="cacheStale">{{ $t('specialPrice.cache_stale_notice') }}</CacheStaleNotice>

    <!-- Filters -->
    <div class="box">
      <div class="columns">
        <div class="column is-4">
          <label class="label">{{ $t('specialPrice.search_label') }}</label>
          <input class="input" type="text" v-model="q" @keyup.enter="fetchData(1)"/>
        </div>
        <div class="column is-2">
          <label class="label">{{ $t('specialPrice.status') }}</label>
          <div class="select">
            <select v-model="status" @change="fetchData(1)">
              <option value="all">{{ $t('common.ທັງໝົດ') }}</option>
              <option value="active">{{ $t('specialPrice.status_active') }}</option>
              <option value="inactive">{{ $t('specialPrice.off') }}</option>
            </select>
          </div>
        </div>
        <div class="column is-1 filter-button">
          <vs-button circle icon flat @click="fetchData(1)"><i class="fal fa-search"></i></vs-button>
        </div>
      </div>
    </div>

    <!-- List -->
    <div class="center fit-width">
      <vs-table ref="table">
        <template #thead>
          <vs-tr>
            <vs-th>{{ $t('specialPrice.no') }}</vs-th>
            <vs-th>{{ $t('specialPrice.program_name') }}</vs-th>
            <vs-th>{{ $t('specialPrice.origin') }}</vs-th>
            <vs-th>{{ $t('specialPrice.destination') }}</vs-th>
            <vs-th>{{ $t('specialPrice.phones_categories_header') }}</vs-th>
            <vs-th>Logistics / kg</vs-th>
            <vs-th>{{ $t('specialPrice.on_off_header') }}</vs-th>
            <vs-th></vs-th>
          </vs-tr>
        </template>
        <template #tbody>
          <vs-tr :key="row.id" v-for="(row, index) in rows">
            <vs-td>{{ rowNumber(index) }}</vs-td>
            <vs-td class="cell-wrap cell-name">
              <strong>{{ row.name }}</strong>
              <div v-if="row.reference" class="cell-sub">{{ row.reference }}</div>
              <div class="cell-sub">
                {{ $t('specialPrice.edited_at', { time: formatDateTime(row.updated_at) }) }}<span v-if="row.updated_by"> · {{ row.updated_by }}</span>
              </div>
            </vs-td>
            <vs-td class="cell-wrap cell-branches">{{ scopeText(row.start_branches, row.start_provinces) }}</vs-td>
            <vs-td class="cell-wrap cell-branches">
              {{ scopeText(row.end_branches, row.end_provinces) }}
              <div v-if="row.route_scope && row.route_scope !== 'any'" class="cell-sub">{{ routeScopeLabel(row.route_scope) }}</div>
            </vs-td>
            <vs-td>
              {{ $t('specialPrice.phone_count', { n: row.customers_count }) }}
              <div class="cell-sub">{{ $t('specialPrice.category_count', { n: row.categories_count }) }}</div>
            </vs-td>
            <vs-td>
              {{ row.logistics_price_per_kg ? formatKip(row.logistics_price_per_kg) : '-' }}
              <div v-if="row.logistics_price_per_kg && hasFloor(row.logistics_weight_above_kg)" class="cell-sub">
                &gt; {{ formatKip(row.logistics_weight_above_kg) }} kg
              </div>
            </vs-td>
            <vs-td>
              <vs-switch :value="row.is_active" :disabled="!!toggling[row.id]" @input="toggle(row, $event)">
                <template #off>{{ $t('specialPrice.off') }}</template>
                <template #on>{{ $t('specialPrice.on') }}</template>
              </vs-switch>
            </vs-td>
            <vs-td style="text-align: right; width: 100px">
              <div class="group-icon">
                <vs-button circle icon flat @click="openForm(row.id)"><i class="fad fa-pencil"></i></vs-button>
                <vs-button circle icon flat danger @click="askDelete(row)"><i class="fad fa-trash-alt"></i></vs-button>
              </div>
            </vs-td>
          </vs-tr>
          <vs-tr v-if="!isLoading && !rows.length">
            <vs-td colspan="8" class="empty-row">{{ $t('specialPrice.empty_list') }}</vs-td>
          </vs-tr>
        </template>
        <template #footer>
          <pagination v-if="pagination.last_page > 1" :pagination="pagination" :offset="10"
                      @paginate="fetchData"></pagination>
          <Loading v-if="isLoading"/>
        </template>
      </vs-table>
    </div>

    <!-- Check a phone -->
    <div class="box check-box">
      <h5><i class="fal fa-search-dollar"></i> {{ $t('specialPrice.check_title') }}</h5>
      <div class="columns">
        <div class="column check-tel">
          <label class="label">{{ $t('specialPrice.sender_tel') }}</label>
          <input class="input" type="text" v-model="check.tel" placeholder="02095363033" @keyup.enter="runCheck()"/>
        </div>
        <div class="column check-branch">
          <label class="label">{{ $t('specialPrice.start_branch') }}</label>
          <BranchPicker v-model="check.start"/>
        </div>
        <div class="column check-branch">
          <label class="label">{{ $t('specialPrice.end_branch_optional') }}</label>
          <BranchPicker v-model="check.end"/>
        </div>
        <div class="column is-narrow filter-button">
          <vs-button :loading="check.loading" :disabled="!check.tel" @click="runCheck()">{{ $t('specialPrice.check_button') }}</vs-button>
        </div>
      </div>
      <div v-if="check.result" class="check-result">
        <template v-if="check.result.matched">
          <p>{{ $t('specialPrice.check_matched_prefix') }} <strong>{{ check.result.tel }}</strong> {{ $t('specialPrice.check_matched_suffix') }}</p>
          <ul>
            <li v-for="category in check.result.categories" :key="category.parcel_category_id">
              {{ categoryName(category.parcel_category_id) }} — <strong>{{ formatKip(category.price) }}</strong> {{ $t('common.ກີບ') }}
              <small class="cell-sub">({{ category.program_name }})</small>
            </li>
            <li v-for="offer in logisticsOffers(check.result)" :key="'l' + offer.program_id">
              Logistics — <strong>{{ formatKip(offer.price_per_kg) }}</strong> {{ $t('common.ກີບ') }}/kg
              (<span v-if="hasFloor(offer.weight_above_kg)">{{ $t('specialPrice.weight_above_kg', { kg: formatKip(offer.weight_above_kg) }) }} </span>{{ $t('specialPrice.when_cheaper_than_list') }})
              <small class="cell-sub">({{ offer.program_name }})</small>
            </li>
          </ul>
        </template>
        <p v-else>{{ $t('specialPrice.check_unmatched_prefix') }} <strong>{{ $t('specialPrice.check_unmatched_not') }}</strong> {{ $t('specialPrice.check_unmatched_suffix') }}</p>
      </div>
    </div>

    <!-- Add / edit -->
    <vs-dialog prevent-close v-model="formOpen" width="1040px">
      <ProgramForm v-if="formOpen"
                   :key="formKey"
                   :program-id="formProgramId"
                   :category-options="categoryOptions"
                   :province-options="provinceOptions"
                   @close="formOpen = false"
                   @saved="onSaved"/>
    </vs-dialog>

    <!-- Delete confirm -->
    <vs-dialog v-model="deleteOpen" width="460px">
      <template #header>
        <h4 class="not-margin">{{ $t('specialPrice.delete_title') }}</h4>
      </template>
      <p v-if="deleting">{{ $t('specialPrice.delete') }} “<strong>{{ deleting.name }}</strong>”? {{ $t('specialPrice.delete_confirm_hint') }}</p>
      <template #footer>
        <div class="form-buttons">
          <vs-button flat @click="deleteOpen = false">{{ $t('specialPrice.cancel') }}</vs-button>
          <vs-button danger :loading="deleteBusy" @click="confirmDelete()">{{ $t('specialPrice.delete') }}</vs-button>
        </div>
      </template>
    </vs-dialog>
  </div>
</template>

<script>
import moment from 'moment';
import Loading from '@/views/Loading';
import pagination from '@/Plugins/Paginations/Pagination';
import CacheStaleNotice from '@/components/CacheStaleNotice';
import BranchPicker from '@/views/Corporate/SpecialPrice/BranchPicker';
import ProgramForm from '@/views/Corporate/SpecialPrice/Dialog/ProgramForm';
import { formatKip, friendlyMessage, routeScopeLabel } from '@/views/Corporate/SpecialPrice/errorText';

/**
 * ລາຄາພິເສດສະເພາະລູກຄ້າ — api/admin/customer-special-prices.
 *
 * The rows here ARE what the freight calculator applies (same table, same service on the
 * backend), so the switch on this page is the real on/off for the deal.
 */
export default {
  name: 'CustomerSpecialPriceList',

  components: { Loading, pagination, CacheStaleNotice, BranchPicker, ProgramForm },

  data: () => ({
    isLoading: false,
    cacheStale: false,
    rows: [],
    pagination: { current_page: 1, last_page: 1, total: 0, per_page: 20 },
    q: '',
    status: 'all',
    categoryOptions: [],
    provinceOptions: [],
    toggling: {},

    formOpen: false,
    formProgramId: null,
    formKey: 0,

    deleteOpen: false,
    deleting: null,
    deleteBusy: false,

    check: { tel: '', start: null, end: null, loading: false, result: null },
    /// Only the latest list / check request may write to the page.
    listSeq: 0,
    checkSeq: 0,
  }),

  watch: {
    /// A shown result belongs to the phone + route it was asked for; any change hides it.
    'check.tel'() { this.clearCheck(); },
    'check.start'() { this.clearCheck(); },
    'check.end'() { this.clearCheck(); },
  },

  created() {
    this.fetchData(1);
    this.fetchCategoryOptions();
    this.fetchProvinceOptions();
  },

  methods: {
    formatKip,
    routeScopeLabel,

    fetchData(page) {
      const seq = ++this.listSeq;
      this.isLoading = true;
      this.$axios.get('customer-special-prices', {
        params: {
          q: this.q,
          status: this.status,
          page: page || this.pagination.current_page || 1,
          per_page: this.pagination.per_page || 20,
        },
      }).then(res => {
        if (seq !== this.listSeq) {
          return;
        }
        const data = (res.data && res.data.data) || {};
        /// Deleting the last row of the last page leaves us past the end — go to the real last page.
        if ((data.current_page || 1) > (data.last_page || 1) && (data.total || 0) > 0) {
          this.fetchData(data.last_page);
          return;
        }
        this.rows = data.rows || [];
        this.pagination = {
          current_page: data.current_page || 1,
          last_page: data.last_page || 1,
          total: data.total || 0,
          per_page: data.per_page || 20,
        };
        this.isLoading = false;
      }).catch(error => {
        if (seq !== this.listSeq) {
          return;
        }
        this.isLoading = false;
        this.notify('danger', friendlyMessage(error));
      });
    },

    fetchCategoryOptions() {
      this.$axios.get('customer-special-prices/category-options').then(res => {
        this.categoryOptions = (res.data && res.data.data) || [];
      }).catch(() => {
        this.categoryOptions = [];
      });
    },

    fetchProvinceOptions() {
      this.$axios.get('customer-special-prices/province-options').then(res => {
        this.provinceOptions = (res.data && res.data.data) || [];
      }).catch(() => {
        this.provinceOptions = [];
      });
    },

    rowNumber(index) {
      return (this.pagination.current_page - 1) * this.pagination.per_page + index + 1;
    },

    /// One side of the route: its branches and its provinces (either one lets a bill through).
    scopeText(branches, provinces) {
      const names = (branches || []).map(branch => branch.name || ('#' + branch.id))
        .concat((provinces || []).map(province => this.$t('specialPrice.province_named', { name: province.name || ('#' + province.id) })));
      return names.length ? names.join(', ') : this.$t('specialPrice.all_branches');
    },

    hasFloor(value) {
      return value !== null && value !== undefined && value !== '';
    },

    /// Every logistics rate with its weight floor; a backend from before floors existed sends only
    /// the single cheapest rate.
    logisticsOffers(result) {
      if (Array.isArray(result.logistics_offers)) {
        return result.logistics_offers;
      }
      return result.logistics_price_per_kg ? [{
        price_per_kg: result.logistics_price_per_kg,
        weight_above_kg: null,
        program_id: result.logistics_program_id,
        program_name: result.logistics_program_name,
      }] : [];
    },

    categoryName(id) {
      const option = this.categoryOptions.find(category => category.id === id);
      return option ? option.name : '#' + id;
    },

    openForm(programId) {
      this.formProgramId = programId;
      this.formKey += 1;
      this.formOpen = true;
    },

    onSaved({ message, cacheStale }) {
      this.formOpen = false;
      this.cacheStale = cacheStale;
      this.notify('success', message || this.$t('specialPrice.saved_success'));
      this.fetchData();
    },

    /**
     * Optimistic switch: flip on screen, PATCH, flip back if the backend refused.
     */
    toggle(row, value) {
      const previous = row.is_active;
      row.is_active = value;
      this.$set(this.toggling, row.id, true);
      this.$axios.patch(`customer-special-prices/${row.id}/status`, { is_active: value }).then(res => {
        this.$delete(this.toggling, row.id);
        this.cacheStale = !!(res.data && res.data.cache_stale);
        this.notify('success', (res.data && res.data.message) || this.$t('specialPrice.saved_success'));
      }).catch(error => {
        this.$delete(this.toggling, row.id);
        row.is_active = previous;
        this.notify('danger', friendlyMessage(error));
      });
    },

    askDelete(row) {
      this.deleting = row;
      this.deleteOpen = true;
    },

    confirmDelete() {
      if (!this.deleting) {
        return;
      }
      this.deleteBusy = true;
      this.$axios.delete(`customer-special-prices/${this.deleting.id}`).then(res => {
        this.deleteBusy = false;
        this.deleteOpen = false;
        this.cacheStale = !!(res.data && res.data.cache_stale);
        this.notify('success', (res.data && res.data.message) || this.$t('specialPrice.deleted_success'));
        this.fetchData();
      }).catch(error => {
        this.deleteBusy = false;
        this.notify('danger', friendlyMessage(error));
      });
    },

    clearCheck() {
      this.checkSeq += 1;
      this.check.result = null;
      this.check.loading = false;
    },

    runCheck() {
      if (!this.check.tel) {
        return;
      }
      const seq = ++this.checkSeq;
      this.check.loading = true;
      this.check.result = null;
      this.$axios.get('customer-special-prices/resolve', {
        params: {
          sender_customer_phone_number: this.check.tel,
          start_branch_id: this.check.start ? this.check.start.id : undefined,
          end_branch_id: this.check.end ? this.check.end.id : undefined,
        },
      }).then(res => {
        if (seq !== this.checkSeq) {
          return;
        }
        this.check.loading = false;
        this.check.result = (res.data && res.data.data) || null;
      }).catch(error => {
        if (seq !== this.checkSeq) {
          return;
        }
        this.check.loading = false;
        this.notify('danger', friendlyMessage(error));
      });
    },

    formatDateTime(value) {
      return value ? moment(value).format('DD/MM/YYYY HH:mm') : '';
    },

    notify(color, text, duration = 4000) {
      this.$notification.openNotification({ title: this.$t('specialPrice.special_price'), text }, 'top-right', color, duration);
    },
  },
};
</script>

<style lang="scss" scoped>
.special-price-page {
  margin-top: 40px;
  /* #app centres its text; labels and hints here sit over left-aligned controls. */
  text-align: left;
}

.page-hint {
  color: #777;
  margin-bottom: 12px;
}

.fit-width {
  white-space: nowrap;
}

.cell-sub {
  color: #888;
  font-size: 12px;
}

/* Long names / branch lists wrap so the edit + delete buttons stay on screen. */
.cell-wrap {
  white-space: normal;
}

.cell-name {
  min-width: 180px;
  max-width: 240px;
}

.cell-branches {
  max-width: 150px;
  /* Lao names carry no spaces — let a long one break instead of running under the next column. */
  overflow-wrap: anywhere;
}

.filter-button {
  display: flex;
  align-items: flex-end;
}

.empty-row {
  text-align: center;
  color: #888;
  padding: 24px;
}

.check-box {
  margin-top: 24px;

  h5 {
    font-weight: bold;
    margin-bottom: 8px;
  }

  /* Same height as the branch pickers beside it. */
  .input {
    height: 40px;
  }
}

/* A phone number needs little room — the two branch pickers share everything that is left. */
@media screen and (min-width: 769px) {
  .check-tel {
    flex: none;
    width: 220px;
  }
}

/* Equal halves whatever is selected: without this a long branch name widens its own column. */
.check-branch {
  min-width: 0;
}

/* Outside a .field a Bulma input falls back to the system font, so Lao text loses BoonBaan. */
.input {
  font-family: $font;
}

.check-result {
  margin-top: 8px;

  ul {
    margin-left: 20px;
    list-style: disc;
  }
}

.form-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
